import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/HirerRegister.css';
import API_BASE_URL from '../config';
import useSafeBack from '../hooks/useSafeBack';

function HirerRegister() {
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(''); // for general API errors
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();
    const safeBack = useSafeBack('/');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        accountType: 'individual',
        companyName: ''
    });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // Validate a single field
    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim() ? '' : 'Full name is required';
            case 'email':
                if (!value.trim()) return 'Email is required';
                return /\S+@\S+\.\S+/.test(value) ? '' : 'Email is invalid';
            case 'password':
                if (!value) return 'Password is required';
                return value.length >= 6 ? '' : 'Password must be at least 6 characters';
            case 'confirmPassword':
                return value === formData.password ? '' : 'Passwords do not match';
            case 'companyName':
                // Only validate if account type is company
                if (formData.accountType === 'company' && !value.trim()) {
                    return 'Company name is required';
                }
                return '';
            default:
                return '';
        }
    };

    // Handle blur event
    const handleBlur = (e) => {
        const { name, value } = e.target;
        const fieldError = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            [name]: fieldError || null
        }));
    };

    // Full validation for submit
    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = 'Full name is required';

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (formData.accountType === 'company' && !formData.companyName.trim()) {
            newErrors.companyName = 'Company name is required';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setApiError('');

        try {
            const response = await fetch(`${API_BASE_URL}/hirer/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password,
                    full_name: formData.name,
                    is_individual: formData.accountType === 'individual',
                    company_name: formData.accountType === 'company' ? formData.companyName : null,
                }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                // FastAPI:
                // - validation errors: { detail: [{ loc: [..., field], msg: "..." }, ...] }
                // - app errors: { detail: "..." }
                const detail = data?.detail;
                if (Array.isArray(detail)) {
                    const fieldErrors = {};
                    for (const item of detail) {
                        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
                        if (!field) continue;
                        fieldErrors[field] = item?.msg || 'Invalid value';
                    }
                    // Map backend field names → frontend form field names
                    const mappedErrors = {
                        name: fieldErrors.full_name,
                        email: fieldErrors.email,
                        password: fieldErrors.password,
                        companyName: fieldErrors.company_name,
                    };
                    // Remove undefined entries
                    Object.keys(mappedErrors).forEach((k) => mappedErrors[k] === undefined && delete mappedErrors[k]);
                    if (Object.keys(mappedErrors).length > 0) setErrors(mappedErrors);
                    else setApiError('Registration failed');
                } else {
                    setApiError(detail || 'Registration failed');
                }
                setLoading(false);
                return;
            }

            // Success – you can either auto‑login or redirect to sign‑in
            alert('Registration successful! Please sign in.');
            navigate('/hirer/signin', { state: { role: 'hirer' } });
        } catch (error) {
            console.error('Registration error:', error);
            setApiError('Network error – please try again.');
            setLoading(false);
        }
    };

    return (
        <div className="register-container">
            <button className="back-button" onClick={safeBack}>← Back</button>
            <h2>Hirer Registration</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    {errors.name && <div className="error">{errors.name}</div>}
                </div>

                <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    {errors.email && <div className="error">{errors.email}</div>}
                </div>

                <div className="form-group">
                    <label htmlFor="password">Password * (min. 6 characters)</label>
                    <div className="password-field">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword((prev) => !prev)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    {errors.password && <div className="error">{errors.password}</div>}
                </div>

                <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password *</label>
                    <div className="password-field">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                            {showConfirmPassword ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    {errors.confirmPassword && <div className="error">{errors.confirmPassword}</div>}
                </div>

                <div className="form-group">
                    <label>Account Type *</label>
                    <div className="radio-group">
                        <label>
                            <input
                                type="radio"
                                name="accountType"
                                value="individual"
                                checked={formData.accountType === 'individual'}
                                onChange={handleChange}
                            /> Individual
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="accountType"
                                value="company"
                                checked={formData.accountType === 'company'}
                                onChange={handleChange}
                            /> Company
                        </label>
                    </div>
                </div>

                {formData.accountType === 'company' && (
                    <div className="form-group">
                        <label htmlFor="companyName">Company Name *</label>
                        <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        {errors.companyName && <div className="error">{errors.companyName}</div>}
                    </div>
                )}

                <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? 'Registering...' : 'Register'}
                </button>
            </form>
            {apiError && <div className="error api-error">{apiError}</div>}
            <p className="signin-prompt">
                Already have an account?{' '}
                <span className="link" onClick={() => navigate('/hirer/signin', { state: { role: 'hirer' } })}>
                    Sign in here
                </span>
            </p>
        </div>
    );
}

export default HirerRegister;